import { Image, Text, View } from "react-native";
import { ActionBanner, MoonToggle, Row, colors } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import { GroupedCard } from "../components";
import { ICONS, ROW_ICONS } from "../icons";
import type { Study } from "../types";

type Props = {
  studies: Study[];
  anonymousDataContribution: boolean;
  anonymousDataContributionLoadFailed: boolean;
  anonymousDataContributionSaveFailed: boolean;
  onAnonymousDataContributionChange: (next: boolean) => void;
};

function StudySummary({ study, divider }: { study: Study; divider: boolean }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: colors.border,
      }}
    >
      <Image
        source={ICONS.beaker}
        resizeMode="contain"
        style={{ width: 28, height: 28, marginTop: 1 }}
      />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
        >
          {study.title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textLight,
            marginTop: 2,
          }}
        >
          {study.compensation
            ? `${study.institution} · ${study.compensation}`
            : study.institution}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textFaint,
            marginTop: 5,
          }}
        >
          {study.matchReason}
        </Text>
      </View>
    </View>
  );
}

function EmptyStudies() {
  const t = useTranslations();

  return (
    <View
      testID="research-studies-empty-state"
      style={{
        alignItems: "center",
        paddingHorizontal: 28,
        paddingVertical: 28,
      }}
    >
      <Image
        source={ROW_ICONS.research}
        resizeMode="contain"
        style={{ width: 42, height: 42, marginBottom: 10 }}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          lineHeight: 20,
          fontWeight: "500",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {t("you.research_studies.empty_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 18,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginTop: 4,
        }}
      >
        {t("you.research_studies.empty_description")}
      </Text>
    </View>
  );
}

/**
 * The dialog title supplies the section heading. This content intentionally
 * contains only the relevant study state and the persisted research choice.
 */
export default function ResearchStudies({
  studies,
  anonymousDataContribution,
  anonymousDataContributionLoadFailed,
  anonymousDataContributionSaveFailed,
  onAnonymousDataContributionChange,
}: Props) {
  const t = useTranslations();
  const hasConsentError =
    anonymousDataContributionLoadFailed || anonymousDataContributionSaveFailed;

  return (
    <View>
      <GroupedCard>
        {studies.length > 0 ? (
          studies.map((study, index) => (
            <StudySummary key={study.id} study={study} divider={index > 0} />
          ))
        ) : (
          <EmptyStudies />
        )}

        <Row
          surface="plain"
          leading={
            <Image
              source={ICONS.anonymousDataContribution}
              resizeMode="contain"
              style={{ width: 36, height: 36 }}
            />
          }
          leadingSize={36}
          title={t("you.research_studies.anonymous_label")}
          subtitle={t("you.research_studies.anonymous_description")}
          trailing={
            <MoonToggle
              value={anonymousDataContribution}
              onChange={onAnonymousDataContributionChange}
              testID="anonymous-data-contribution-toggle"
              accessibilityLabel={t("you.research_studies.anonymous_label")}
              accessibilityHint={t(
                "you.research_studies.anonymous_description",
              )}
            />
          }
          gap={12}
          paddingHorizontal={16}
          paddingVertical={14}
          borderRadius={0}
          titleStyle={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
          subtitleStyle={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textFaint,
          }}
          style={{
            borderRadius: 0,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        />
      </GroupedCard>

      {hasConsentError ? (
        <ActionBanner
          variant="important"
          message={t(
            anonymousDataContributionLoadFailed
              ? "you.research_studies.contribution_load_failed"
              : "you.research_studies.contribution_save_failed",
          )}
        />
      ) : null}
    </View>
  );
}
